import moment from "moment";
import type { Consultant, JobSchedule } from "@models/Schedule";
import { isShiftWithinAbsentDate } from "@utils/dateUtils";

export interface WeekScheduleShift {
	id: string;
	start: string;
	end: string;
	break?: number;
	status?: string;
	/** Not saved yet: added in the single-job view and sent together later */
	isDraft?: boolean;
}

/** A shift added locally, sent to the backend when the drafts are submitted */
export interface WeekScheduleDraft {
	/** Local id, never sent */
	id: string;
	start: string;
	end: string;
	break?: number;
}

export type WeekScheduleCellVariant = "booked" | "unmanned" | "absent" | "declined" | "requestDeclined" | "draft";

/** One job's shifts in one week. Who works a shift is looked up per shift. */
export interface WeekScheduleRow {
	key: string;
	job: JobSchedule;
	/** Shifts keyed by YYYY-MM-DD, sorted by start time */
	shiftsByDate: Record<string, WeekScheduleShift[]>;
}

export const dateKey = (date: string | Date | moment.Moment) => moment(date).format("YYYY-MM-DD");

/** Monday..Sunday of the ISO week that `date` belongs to */
export const getWeekDates = (date: Date | moment.Moment): moment.Moment[] => {
	const monday = moment(date).startOf("isoWeek");
	return Array.from({ length: 7 }, (_, i) => monday.clone().add(i, "day"));
};

/** Mondays (YYYY-MM-DD) from the week of `start` through the week of `end` */
export const getWeekStartsBetween = (start: string, end: string): string[] => {
	const weeks: string[] = [];
	const last = moment(end).startOf("isoWeek");
	for (const week = moment(start).startOf("isoWeek"); week.isSameOrBefore(last); week.add(1, "week")) weeks.push(dateKey(week));
	return weeks;
};

export const getGrossHours = (shift: WeekScheduleShift) => moment(shift.end).diff(moment(shift.start), "minutes") / 60;

export const formatHours = (hours: number) => hours.toLocaleString("sv-SE", { minimumFractionDigits: 1, maximumFractionDigits: 2 });

export const getConsultantForShift = (job: JobSchedule, shiftId: string): Consultant | undefined => {
	const application = job.applications?.find((a) => a.shifts?.some((s) => s.id === shiftId));
	const consultant = application?.consultant as Consultant | undefined;
	return consultant?.firstName !== undefined ? consultant : undefined;
};

export const getCellVariant = (
	job: JobSchedule,
	shift: WeekScheduleShift,
	consultant?: Consultant,
	isRequestDeclined = false,
): WeekScheduleCellVariant => {
	if (job.status === "DECLINED") return "declined";
	if (shift.isDraft) return "draft";
	if (!consultant) return isRequestDeclined ? "requestDeclined" : "unmanned";
	if (isShiftWithinAbsentDate(shift, consultant.absences)) return "absent";
	return "booked";
};

/**
 * Builds the rows for one week: one per job with shifts that week, in the
 * order the jobs came. Parallel shifts on a day end up as several pills.
 * With `keepEmpty` every job gets a row, so its empty days can be clicked.
 */
export const getRowsForWeek = (jobs: JobSchedule[], weekDates: moment.Moment[], keepEmpty = false): WeekScheduleRow[] => {
	const weekKeys = new Set(weekDates.map(dateKey));
	const rows: WeekScheduleRow[] = [];

	jobs.forEach((job) => {
		const row: WeekScheduleRow = { key: job.id, job, shiftsByDate: {} };
		[...(job.shifts as WeekScheduleShift[])]
			.sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf())
			.forEach((shift) => {
				const key = dateKey(shift.start);
				if (weekKeys.has(key)) (row.shiftsByDate[key] = row.shiftsByDate[key] || []).push(shift);
			});
		if (keepEmpty || Object.keys(row.shiftsByDate).length) rows.push(row);
	});

	return rows;
};

/**
 * The consultant the backend asks to take shifts added to a job: the one
 * booked on the job's last shift. The schedule only carries accepted applications.
 */
export const getExtensionConsultant = (job: JobSchedule): Consultant | undefined => {
	const lastBooked = [...job.shifts]
		.filter((shift) => getConsultantForShift(job, shift.id))
		.sort((a, b) => moment(b.start).valueOf() - moment(a.start).valueOf())[0];
	return lastBooked ? getConsultantForShift(job, lastBooked.id) : undefined;
};

/**
 * Extension requests the consultant declined (or let expire). The backend keeps
 * the shift as EXTEND_REQUESTED, only the invitation tells; a later open
 * invitation for the same shift means it was asked again.
 */
export const getRequestDeclinedShiftIds = (
	shifts: WeekScheduleShift[],
	invitations: { status: string; shifts?: { id: string }[] }[],
): Set<string> => {
	const openIds = new Set(invitations.filter((i) => i.status === "OPEN").flatMap((i) => (i.shifts || []).map((s) => s.id)));
	const closedIds = new Set(invitations.filter((i) => i.status !== "OPEN").flatMap((i) => (i.shifts || []).map((s) => s.id)));
	return new Set(
		shifts.filter((s) => s.status === "EXTEND_REQUESTED" && closedIds.has(s.id) && !openIds.has(s.id)).map((s) => s.id),
	);
};

/** Times (HH:mm) and break of the shift that starts last, as the default for a new one */
export const getLatestShiftTimes = (shifts: { start: string; end: string; break?: number }[]) => {
	const latest = [...shifts].sort((a, b) => moment(b.start).valueOf() - moment(a.start).valueOf())[0];
	if (!latest) return undefined;
	return { from: moment(latest.start).format("HH:mm"), to: moment(latest.end).format("HH:mm"), break: latest.break || 0 };
};

let draftCounter = 0;
export const nextDraftId = () => `draft-${Date.now()}-${++draftCounter}`;

const UNMANNED_LANE = "unmanned";

const getLaneKey = (job: JobSchedule, shift: WeekScheduleShift) => getConsultantForShift(job, shift.id)?.id ?? UNMANNED_LANE;

/**
 * Lanes of one job's week: its consultants in order of first appearance, with
 * unmanned shifts last. A consultant keeps the same lane in every day cell, so
 * their shifts line up across the week.
 */
export const getWeekLanes = (row: WeekScheduleRow, weekDates: moment.Moment[]): string[] => {
	const lanes: string[] = [];
	weekDates.forEach((date) =>
		(row.shiftsByDate[dateKey(date)] || []).forEach((shift) => {
			const lane = getLaneKey(row.job, shift);
			if (lane !== UNMANNED_LANE && !lanes.includes(lane)) lanes.push(lane);
		}),
	);
	return [...lanes, UNMANNED_LANE];
};

/**
 * A day's shifts grouped per lane. Empty lanes above a shift become spacers
 * in the cell; empty lanes below the last shift are dropped so cells only
 * grow where needed.
 */
export const getLaneSlots = (row: WeekScheduleRow, key: string, lanes: string[]): WeekScheduleShift[][] => {
	const shifts = row.shiftsByDate[key] || [];
	const slots = lanes.map((lane) => shifts.filter((shift) => getLaneKey(row.job, shift) === lane));
	while (slots.length && !slots[slots.length - 1].length) slots.pop();
	return slots;
};

export const getInitials = (consultant: { firstName?: string; lastName?: string }) =>
	[consultant.firstName, consultant.lastName]
		.map((name) => name?.trim()[0] ?? "")
		.join("")
		.toUpperCase();

/** Hours per day, leaving out declined jobs since those shifts won't be worked */
export const getDayTotals = (rows: WeekScheduleRow[], key: string) => {
	let hours = 0;
	let breakHours = 0;
	rows.forEach((row) => {
		if (row.job.status === "DECLINED") return;
		(row.shiftsByDate[key] || []).forEach((shift) => {
			hours += getGrossHours(shift) - (shift.break || 0);
			breakHours += shift.break || 0;
		});
	});
	return { hours, breakHours };
};

export type ShiftRepeatPreset = "weekdays" | "wholeWeek" | "nextWeek" | "fourWeeks";

export const REPEAT_PRESETS: { key: ShiftRepeatPreset; label: string }[] = [
	{ key: "weekdays", label: "Vardagar samma vecka" },
	{ key: "wholeWeek", label: "Alla dagar samma vecka" },
	{ key: "nextWeek", label: "Samma dag nästa vecka" },
	{ key: "fourWeeks", label: "Samma dag 4 veckor framåt" },
];

/** Dates (YYYY-MM-DD) a shift is copied to for a repeat preset. Never includes past dates. */
export const getRepeatDates = (shiftStart: string, preset: ShiftRepeatPreset): string[] => {
	const start = moment(shiftStart);
	let dates: moment.Moment[] = [];
	if (preset === "weekdays" || preset === "wholeWeek") {
		dates = getWeekDates(start)
			.slice(0, preset === "weekdays" ? 5 : 7)
			.filter((d) => !d.isSame(start, "day"));
	} else {
		const weeks = preset === "nextWeek" ? 1 : 4;
		dates = Array.from({ length: weeks }, (_, i) => start.clone().add(i + 1, "week"));
	}
	return dates.filter((d) => d.isSameOrAfter(moment(), "day")).map(dateKey);
};

/** Moves a shift's start/end to another date, keeping the times (and overnight ends) */
export const moveShiftToDate = (shift: { start: string; end: string }, date: string) => {
	const dayDiff = moment(date).startOf("day").diff(moment(shift.start).startOf("day"), "days");
	return {
		start: moment(shift.start).add(dayDiff, "days").toISOString(),
		end: moment(shift.end).add(dayDiff, "days").toISOString(),
	};
};

/** Builds start/end ISO strings from a date and HH:mm times. An end before start means next day. */
export const buildShiftTimes = (date: string, from: string, to: string) => {
	const start = moment(`${date} ${from}`, "YYYY-MM-DD HH:mm");
	const end = moment(`${date} ${to}`, "YYYY-MM-DD HH:mm");
	if (!end.isAfter(start)) end.add(1, "day");
	return { start: start.toISOString(), end: end.toISOString() };
};

/** Same default as the rest of the app: 0,5 h break for shifts of 5 h or more */
export const getDefaultBreak = (from: string, to: string) => {
	const { start, end } = buildShiftTimes("2000-01-01", from, to);
	return moment(end).diff(moment(start), "minutes") / 60 >= 5 ? 0.5 : 0;
};
